import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LoginForm } from "@/components/auth/LoginForm";
import { RegisterFlow } from "@/components/auth/RegisterFlow";
import { Logo } from "@/components/layout/Logo";
import { ShieldCheck, XCircle, CheckCircle2 } from "lucide-react";

function isValidOrigin(o) {
  try {
    const u = new URL(o);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

export default function WidgetLogin() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const state = params.get("state") || "";
  const origin = params.get("origin") || "";
  const [allowed, setAllowed] = useState(null); // null=checking, true/false
  const [done, setDone] = useState(false);
  const mode = params.get("m") === "register" ? "register" : "login"; // URL-driven
  const [regStage, setRegStage] = useState("email");

  const goRegister = () => { const p = new URLSearchParams(params); p.set("m", "register"); setParams(p); };
  const goLogin = () => { const p = new URLSearchParams(params); p.delete("m"); setParams(p); };

  useEffect(() => {
    if (!isValidOrigin(origin) || !state) { setAllowed(false); return; }
    api.get("/widget/config")
      .then(({ data }) => {
        const list = (data.allowed_origins || []).map((o) => o.replace(/\/$/, ""));
        // Fail CLOSED: only allow-listed origins may receive the auth token.
        setAllowed(list.includes(origin.replace(/\/$/, "")));
      })
      .catch(() => setAllowed(false));
  }, [origin, state]);

  const posted = useRef(false);
  const postToken = async () => {
    if (!getToken() || posted.current) return;
    posted.current = true;
    let token;
    try {
      // Scoped token (favorites only, 24h, bound to this origin) — never the portal session token.
      ({ data: { token } } = await api.post("/auth/widget-token", { origin: origin.replace(/\/$/, "") }));
    } catch {
      posted.current = false;
      setAllowed(false);
      return;
    }
    // Post ONLY to the exact requesting origin — never '*'.
    window.opener?.postMessage({ type: "myelite-auth", state, token }, origin);
    setDone(true);
    setTimeout(() => window.close(), 800);
  };

  // If already authenticated when the popup opens (or after register), hand the token back.
  useEffect(() => {
    if (user && allowed === true && !done) postToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, allowed]);

  const wideReg = mode === "register" && (regStage === "typeform" || regStage === "fetching");

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy p-4">
      <div className={`w-full ${mode === "register" ? (wideReg ? "max-w-2xl" : "max-w-md") : "max-w-sm"} bg-white rounded-3xl shadow-pop p-6 sm:p-8`} data-testid="widget-login">
        <div className="flex justify-center mb-6"><Logo /></div>

        {allowed === null && <p className="text-center text-muted-foreground">Loading…</p>}

        {allowed === false && (
          <div className="text-center py-6" data-testid="widget-login-blocked">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-3" />
            <h1 className="font-display font-black italic text-xl text-navy uppercase">Unauthorized origin</h1>
            <p className="text-muted-foreground mt-2 text-sm">This site is not allowed to connect to Elite. Ask the administrator to whitelist it.</p>
          </div>
        )}

        {allowed === true && done && (
          <div className="text-center py-6">
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-3" />
            <h1 className="font-display font-black italic text-xl text-navy uppercase">You're in!</h1>
            <p className="text-muted-foreground mt-2 text-sm">Returning to the page…</p>
          </div>
        )}

        {allowed === true && !done && mode === "login" && (
          <>
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-orange/10 mb-5">
              <ShieldCheck className="h-5 w-5 text-orange shrink-0" />
              <p className="text-xs text-navy">Sign in to save this to your Elite favorites.</p>
            </div>
            <LoginForm onSuccess={postToken} showRegister={false} showForgot={false} />
            <p className="text-center text-xs text-muted-foreground mt-5">
              No account?{" "}
              <button type="button" onClick={goRegister} className="font-bold text-navy hover:text-orange" data-testid="widget-to-register">Create one now</button>
            </p>
          </>
        )}

        {allowed === true && !done && mode === "register" && (
          <RegisterFlow compact onSuccess={postToken} onStageChange={setRegStage} onLoginClick={goLogin} />
        )}
      </div>
    </div>
  );
}
